import React, { useEffect, useState } from 'react';
import { Modal, Button, ConfigProvider } from 'antd';
import { X as CloseIcon } from 'lucide-react';
import { estimateWithdrawFee, WithdrawFeeEstimate } from '../../api/payment/transaction.api';

const BRAND = '#066BFF';
const fmt = (n: number) => `${n}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

interface Props {
  open: boolean;
  raised: number;
  /** Phần chủ chiến dịch tự ủng hộ: máy chủ trừ ra trước khi tạo lệnh rút. */
  selfDonated?: number;
  onClose: () => void;
  onConfirm: () => void;
}

const Row: React.FC<{ label: string; value: string; strong?: boolean }> = ({
  label,
  value,
  strong,
}) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
    <span className="text-[15px] text-[#51545a]">{label}</span>
    <span
      className={`text-[16px] ${strong ? 'font-semibold text-[#066BFF]' : 'font-medium text-[#212b4e]'}`}
    >
      {value}
    </span>
  </div>
);

export const WithdrawModal: React.FC<Props> = ({
  open,
  raised,
  selfDonated = 0,
  onClose,
  onConfirm,
}) => {
  const withdrawable = Math.max(0, raised - selfDonated);
  // Rà soát 28/10: trước đây hiện phí cố định 5% tính bằng Ca, trong khi máy chủ thu theo biểu phí rút chung
  // (phí xử lý % lần đầu/lần sau + phí cố định, có thể có thuế). Lấy số xem trước từ API ước tính phí của máy chủ.
  const [estimate, setEstimate] = useState<WithdrawFeeEstimate | null>(null);
  useEffect(() => {
    if (!open || withdrawable <= 0) {
      setEstimate(null);
      return;
    }
    estimateWithdrawFee(withdrawable)
      .then(setEstimate)
      .catch(() => setEstimate(null));
  }, [open, withdrawable]);

  return (
    <ConfigProvider
      theme={{ token: { fontFamily: "'Be Vietnam Pro', inter, sans-serif" } }}
    >
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={500}
        centered
        closeIcon={<CloseIcon size={20} className="text-slate-400" />}
        styles={{ content: { borderRadius: 20, padding: 24 }, body: { paddingTop: 8 } }}
      >
        <h2 className="text-center text-[24px] font-semibold leading-8 tracking-tight mb-4">
          Yêu cầu quyết toán thù lao
        </h2>

        <Row label="Tổng Ca đã nhận" value={`${fmt(raised)} ca`} />
        {selfDonated > 0 && (
          <>
            <Row label="Phần bạn tự ủng hộ" value={`-${fmt(selfDonated)} ca`} />
            <Row label="Số Ca được rút" value={`${fmt(withdrawable)} ca`} />
          </>
        )}
        {estimate ? (
          <>
            <Row label="Phí cố định" value={`${fmt(estimate.fixedFeeCa)} ca`} />
            <Row
              label={`Phí xử lý (${estimate.feePercent}%${estimate.isFirstWithdraw ? ' · lần đầu' : ''})`}
              value={`${fmt(estimate.percentFeeCa)} ca`}
            />
            {estimate.personalIncomeTaxCa > 0 && (
              <Row label="Thuế TNCN" value={`${fmt(estimate.personalIncomeTaxCa)} ca`} />
            )}
            <Row label="Thực nhận (ước tính)" value={`${fmt(estimate.netVnd)}đ`} strong />
          </>
        ) : (
          <p className="text-[13px] text-slate-500 py-3">
            Phí rút tính theo biểu phí chung (phí xử lý % và phí cố định), số chính xác hiện khi máy chủ trả ước tính.
          </p>
        )}
        <p className="text-[12px] text-slate-500 mt-2">
          Phần bạn tự ủng hộ chiến dịch của mình không rút được và không tính vào lệnh rút.
        </p>

        <p className="text-[13px] text-red-500 mt-3">
          *Sau khi quyết toán, chiến dịch sẽ được đóng vĩnh viễn
        </p>

        <div className="flex justify-center gap-3 mt-5">
          <Button
            size="large"
            onClick={onClose}
            className="!rounded-full !px-8"
            style={{ fontFamily: 'inter, sans-serif' }}
          >
            Huỷ
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="!rounded-full !px-8"
            style={{ background: BRAND, borderColor: BRAND, fontFamily: 'inter, sans-serif' }}
          >
            Quyết toán thù lao
          </Button>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default WithdrawModal;
